import React, { useState } from "react";
import { Autocomplete, TextField, Popper, Paper } from "@mui/material";

interface OptionType {
  label: string;
  value: string | number;
}

interface OfferCustomSelectProps {
  options?: OptionType[];
  label?: string;
  value?: OptionType | OptionType[] | null;
  onChange?: (newValue: OptionType | OptionType[] | null) => void;
  multiple?: boolean;
  width?: number | string;
  height?: number | string;
  backgroundColor?: string;
  borderColor?: string;
  borderRadius?: string;
  fontSize?: string;
  fontWeight?: number;
  textColor?: string;
  hoverBgColor?: string;
  hoverBorderColor?: string;
  renderOption?: any;
  disableClearable?: boolean;
  freeSolo?: boolean;
  filterOptions?: boolean;
}

const OfferCustomSelect: React.FC<OfferCustomSelectProps> = ({
  options = [],
  label = "Select option",
  value = null,
  onChange = (value: OptionType) => {},
  multiple = false,
  width = 180,
  height = 40,
  backgroundColor = "#ffffff",
  borderColor = "#ccc",
  borderRadius = "5px",
  fontSize = "14px",
  fontWeight = 500,
  textColor = "#666666",
  hoverBgColor = "#f5f5f5",
  hoverBorderColor = "#bdbdbd",
  renderOption = null,
  disableClearable = true,
  freeSolo = false,
  filterOptions = false,
  ...autocompleteProps
}) => {
  const [open, setOpen] = useState(false);

  const CustomPopper = (props: any) => {
    return (
      <Popper
        {...props}
        placement="bottom-start"
        style={{
          width: props.style?.width,
          zIndex: 1300,
        }}
      />
    );
  };

  const CustomPaper = (props: any) => (
    <Paper
      {...props}
      sx={{
        "& .MuiAutocomplete-listbox": {
          overflowY: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(0,0,0,0.3) transparent",

          "&::-webkit-scrollbar": {
            width: 8,
            borderRadius: 20,
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.3)",
            borderRadius: 20,
            minHeight: 20,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
            borderRadius: 20,
          },
        },
      }}
    />
  );

  const customFilterOptions = filterOptions
    ? undefined
    : (options: OptionType[], state: any) => options;
  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={value}
      onChange={(event, newValue: any) => {
        onChange(newValue);
        if (!multiple) setOpen(false);
      }}
      multiple={multiple}
      disableClearable={disableClearable}
      freeSolo={freeSolo}
      filterOptions={customFilterOptions}
      PopperComponent={CustomPopper}
      PaperComponent={CustomPaper}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={
            !open && (!value || (Array.isArray(value) && value.length === 0))
              ? label
              : ""
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: <>{params.InputProps.endAdornment}</>,
            sx: {
              "& input::placeholder": {
                textAlign: "center",
                fontSize: 13,
              },
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: height,
              width: width,
              fontSize: fontSize,
              backgroundColor: backgroundColor,
              color: textColor,
              borderRadius: borderRadius,
              fontWeight: fontWeight,
              display: "flex",
              alignItems: "center",
              "&:hover": {
                borderColor: hoverBorderColor,
                backgroundColor: hoverBgColor,
              },
              "&.Mui-focused": {
                borderColor: hoverBorderColor,
                backgroundColor: hoverBgColor,
              },
              "& .MuiInputBase-input": {
                textAlign: "left",
                padding: "0 14px !important",
              },
              "& .MuiAutocomplete-clearIndicator": {
                display: "none",
              },
            },
            "& .MuiInputLabel-root": {
              display: "none",
            },
          }}
        />
      )}
      renderOption={
        renderOption ||
        ((props: any, option: OptionType) => (
          <li {...props} key={option.value}>
            {option.label}
          </li>
        ))
      }
      {...autocompleteProps}
    />
  );
};

export default OfferCustomSelect;
